import { Component, computed, inject, signal } from '@angular/core';
import { PermissionService } from '../../permissions/permission.service';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MediaAssetDto, MediaService } from '../../../shared/services/media.service';
import { PagesService } from '../../../shared/services/pages.service';
import { CreateTestimonialPayload, TestimonialDto, TestimonialsService } from '../../../shared/services/testimonials.service';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminEmptyStateComponent,
  AdminIconComponent,
  AdminModalComponent,
  AdminPageHeaderComponent,
  AdminSkeletonComponent,
  AdminTableComponent,
} from '../../../shared/admin';

const TABLE_PAGE_SIZE = 10;
const REVIEW_SNIPPET_MAX = 100;

const DEFAULT_SECTION_TITLE = 'Student Testimonials';
const DEFAULT_SECTION_SUBTITLE =
  'Hear from students who achieved their study abroad goals with Career Grad Global.';

type SectionHeadingForm = {
  testimonialsTitle: string;
  testimonialsSubtitle: string;
};

const emptyForm = (): CreateTestimonialPayload => ({
  studentName: '',
  pill: '',
  review: '',
  rating: 5,
  country: '',
  university: '',
  course: '',
  status: 'PUBLISHED',
});

const emptySectionForm = (): SectionHeadingForm => ({
  testimonialsTitle: DEFAULT_SECTION_TITLE,
  testimonialsSubtitle: DEFAULT_SECTION_SUBTITLE,
});

@Component({
  selector: 'app-testimonials-page',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    AdminPageHeaderComponent,
    AdminButtonComponent,
    AdminCardComponent,
    AdminSkeletonComponent,
    AdminModalComponent,
    AdminEmptyStateComponent,
    AdminTableComponent,
    AdminIconComponent,
  ],
  templateUrl: './testimonials-page.component.html',
})
export class TestimonialsPageComponent {
  private readonly service = inject(TestimonialsService);
  private readonly media = inject(MediaService);
  private readonly pages = inject(PagesService);
  private readonly permissions = inject(PermissionService);

  readonly showCreate = computed(() => this.permissions.canShow('website.testimonials', 'create'));
  readonly canCreate = computed(() => this.permissions.canExecute('website.testimonials', 'create'));
  readonly showEdit = computed(() => this.permissions.canShow('website.testimonials', 'edit'));
  readonly canEdit = computed(() => this.permissions.canExecute('website.testimonials', 'edit'));
  readonly showDelete = computed(() => this.permissions.canShow('website.testimonials', 'delete'));
  readonly canDelete = computed(() => this.permissions.canExecute('website.testimonials', 'delete'));
  readonly showUpload = computed(() => this.permissions.canShow('website.testimonials', 'upload'));
  readonly canUpload = computed(() => this.permissions.canExecute('website.testimonials', 'upload'));
  readonly canSaveModal = computed(() =>
    this.editingId() ? this.canEdit() : this.canCreate(),
  );
  readonly showSaveModal = computed(() =>
    this.editingId() ? this.showEdit() : this.showCreate(),
  );

  readonly starRange = [1, 2, 3, 4, 5] as const;
  readonly items = signal<TestimonialDto[]>([]);
  readonly loading = signal(true);
  readonly showForm = signal(false);
  readonly editingId = signal<string | null>(null);
  readonly saving = signal(false);
  readonly savingSection = signal(false);
  readonly error = signal<string | null>(null);
  readonly sectionError = signal<string | null>(null);
  readonly loadError = signal<string | null>(null);
  readonly profilePreviewUrl = signal<string | null>(null);
  readonly profilePhotoStatus = signal<'none' | 'selected' | 'uploaded' | 'saved'>('none');
  readonly profileUploading = signal(false);
  readonly profileUploadMessage = signal<string | null>(null);
  readonly profilePreviewUrls = signal<Record<string, string>>({});
  readonly tablePage = signal(0);

  form = emptyForm();
  sectionForm = emptySectionForm();
  profileFile: File | null = null;
  private localPreviewObjectUrl: string | null = null;

  get paginatedItems(): TestimonialDto[] {
    const start = this.tablePage() * TABLE_PAGE_SIZE;
    return this.items().slice(start, start + TABLE_PAGE_SIZE);
  }

  get paginationLabel(): string {
    const total = this.items().length;
    if (total === 0) return 'Showing 0 of 0 testimonials';
    const start = this.tablePage() * TABLE_PAGE_SIZE + 1;
    const end = Math.min((this.tablePage() + 1) * TABLE_PAGE_SIZE, total);
    return `Showing ${start}–${end} of ${total} testimonials`;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.items().length / TABLE_PAGE_SIZE));
  }

  get visiblePages(): number[] {
    const total = this.totalPages;
    const current = this.tablePage() + 1;
    const windowSize = Math.min(5, total);
    let start = Math.max(1, current - Math.floor(windowSize / 2));
    const end = Math.min(total, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  get canPrevPage(): boolean {
    return this.tablePage() > 0;
  }

  get canNextPage(): boolean {
    return (this.tablePage() + 1) * TABLE_PAGE_SIZE < this.items().length;
  }

  constructor() {
    this.refresh();
  }

  refresh(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.loadSectionHeading();
    this.service.listPlatform().subscribe({
      next: (items) => {
        this.items.set(items);
        this.clampTablePage();
        this.loadProfilePreviews();
        this.loading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.loading.set(false);
        this.loadError.set('Could not load testimonials.');
      },
    });
  }

  saveSectionHeading(): void {
    if (!this.canEdit()) return;
    this.savingSection.set(true);
    this.sectionError.set(null);
    this.pages
      .patchPlatformMarketingHome({
        testimonialsTitle: this.sectionForm.testimonialsTitle.trim() || undefined,
        testimonialsSubtitle: this.sectionForm.testimonialsSubtitle.trim() || undefined,
      })
      .subscribe({
        next: (d) => {
          this.applySectionFromDoc(d);
          this.savingSection.set(false);
        },
        error: (err) => {
          this.savingSection.set(false);
          this.sectionError.set(err?.error?.message ?? 'Could not save section heading.');
        },
      });
  }

  openCreate(): void {
    if (!this.canCreate()) return;
    this.form = emptyForm();
    this.editingId.set(null);
    this.error.set(null);
    this.resetProfileUpload();
    this.showForm.set(true);
  }

  edit(item: TestimonialDto): void {
    if (!this.canEdit()) return;
    this.form = {
      studentName: item.studentName,
      pill: item.pill ?? '',
      review: item.review,
      rating: item.rating,
      country: item.country ?? '',
      university: item.university ?? '',
      course: item.course ?? '',
      profileImageId: item.profileImageId,
      status: item.status ?? 'PUBLISHED',
    };
    this.editingId.set(item._id);
    this.error.set(null);
    this.resetProfileUpload();
    this.loadProfilePreview(item.profileImageId);
    this.showForm.set(true);
  }

  remove(item: TestimonialDto): void {
    if (!this.canDelete()) return;
    if (!confirm(`Delete testimonial from ${item.studentName}?`)) return;
    this.service.remove(item._id).subscribe(() => this.refresh());
  }

  closeForm(): void {
    this.showForm.set(false);
    this.resetProfileUpload();
  }

  prevPage(): void {
    if (this.canPrevPage) this.tablePage.update((p) => p - 1);
  }

  nextPage(): void {
    if (this.canNextPage) this.tablePage.update((p) => p + 1);
  }

  goToPage(page: number): void {
    const index = page - 1;
    if (index >= 0 && index < this.totalPages) {
      this.tablePage.set(index);
    }
  }

  profilePreview(id: string): string | null {
    return this.profilePreviewUrls()[id] ?? null;
  }

  initials(name: string): string {
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  shortId(id: string): string {
    if (id.length <= 14) return id;
    return `${id.slice(0, 6)}…${id.slice(-4)}`;
  }

  ratingLabel(rating: number): string {
    return `${Number(rating).toFixed(1)}`;
  }

  reviewSnippet(review: string): string {
    const text = review.trim();
    if (text.length <= REVIEW_SNIPPET_MAX) return text;
    return `${text.slice(0, REVIEW_SNIPPET_MAX - 1)}…`;
  }

  onProfileFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.profileFile = input.files?.[0] ?? null;
    this.profileUploadMessage.set(null);
    this.revokeLocalPreview();
    if (this.profileFile) {
      this.localPreviewObjectUrl = URL.createObjectURL(this.profileFile);
      this.profilePreviewUrl.set(this.localPreviewObjectUrl);
      this.profilePhotoStatus.set('selected');
    } else if (!this.form.profileImageId) {
      this.profilePreviewUrl.set(null);
      this.profilePhotoStatus.set('none');
    }
  }

  profilePhotoStatusLabel(): string {
    switch (this.profilePhotoStatus()) {
      case 'selected':
        return 'Photo selected — click Upload photo, then Save.';
      case 'uploaded':
        return 'Photo uploaded — click Save to attach it to this testimonial.';
      case 'saved':
        return 'Photo saved on this testimonial.';
      default:
        return 'No profile photo uploaded yet.';
    }
  }

  uploadProfile(): void {
    if (!this.profileFile) return;
    this.profileUploading.set(true);
    this.profileUploadMessage.set(null);
    const altText = this.form.studentName?.trim()
      ? `Testimonial – ${this.form.studentName.trim()}`
      : 'Testimonial profile photo';
    this.media.upload(this.profileFile, { altText }).subscribe({
      next: (asset) => {
        const assetId = this.mediaAssetId(asset);
        if (!assetId) {
          this.profileUploading.set(false);
          this.profileUploadMessage.set('Upload succeeded but no asset id was returned.');
          return;
        }
        this.form.profileImageId = assetId;
        this.revokeLocalPreview();
        this.profilePreviewUrl.set(this.mediaAssetPreviewUrl(asset));
        this.profileFile = null;
        this.profileUploading.set(false);
        this.profilePhotoStatus.set('uploaded');
        this.profileUploadMessage.set('Photo uploaded. Click Save to store it with this testimonial.');
      },
      error: (err) => {
        this.profileUploading.set(false);
        this.profileUploadMessage.set(err?.error?.message ?? 'Upload failed.');
      },
    });
  }

  clearProfile(): void {
    this.form.profileImageId = undefined;
    this.revokeLocalPreview();
    this.profilePreviewUrl.set(null);
    this.profileFile = null;
    this.profilePhotoStatus.set('none');
    this.profileUploadMessage.set('Photo removed. Click Save to apply.');
  }

  save(): void {
    const profileImageId = this.form.profileImageId?.trim();
    const pill = this.form.pill?.trim();
    const payload: CreateTestimonialPayload = {
      studentName: this.form.studentName?.trim() ?? '',
      review: this.form.review?.trim() ?? '',
      rating: Math.round(Number(this.form.rating)) || 5,
      status: this.form.status,
    };
    if (pill) payload.pill = pill;
    else if (this.editingId()) payload.pill = '';

    const country = this.form.country?.trim();
    if (country) payload.country = country;
    else if (this.editingId()) payload.country = '';

    const university = this.form.university?.trim();
    if (university) payload.university = university;
    else if (this.editingId()) payload.university = '';

    const course = this.form.course?.trim();
    if (course) payload.course = course;
    else if (this.editingId()) payload.course = '';

    if (profileImageId) payload.profileImageId = profileImageId;
    else if (this.editingId()) payload.profileImageId = '';

    this.saving.set(true);
    this.error.set(null);
    const id = this.editingId();
    const op$ = id ? this.service.update(id, payload) : this.service.create(payload);
    op$.subscribe({
      next: () => {
        this.saving.set(false);
        this.showForm.set(false);
        this.refresh();
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(this.formatSaveError(err));
      },
    });
  }

  private formatSaveError(err: unknown): string {
    const body = (err as { error?: { message?: string; errors?: unknown } })?.error;
    const errors = body?.errors;
    if (Array.isArray(errors) && errors.length) {
      return errors
        .map((e) => (typeof e === 'string' ? e : JSON.stringify(e)))
        .join(' ');
    }
    if (errors && typeof errors === 'object') {
      return Object.values(errors as Record<string, unknown>)
        .flat()
        .map(String)
        .join(' ');
    }
    return body?.message ?? 'Save failed.';
  }

  private loadSectionHeading(): void {
    this.pages.getPlatformMarketingHome().subscribe({
      next: (d) => this.applySectionFromDoc(d),
      error: () => {
        this.sectionForm = emptySectionForm();
      },
    });
  }

  private applySectionFromDoc(d: Record<string, unknown>): void {
    this.sectionForm = {
      testimonialsTitle: String(d['testimonialsTitle'] ?? '').trim() || DEFAULT_SECTION_TITLE,
      testimonialsSubtitle: String(d['testimonialsSubtitle'] ?? '').trim() || DEFAULT_SECTION_SUBTITLE,
    };
  }

  private clampTablePage(): void {
    const maxPage = Math.max(0, Math.ceil(this.items().length / TABLE_PAGE_SIZE) - 1);
    if (this.tablePage() > maxPage) {
      this.tablePage.set(maxPage);
    }
  }

  private loadProfilePreviews(): void {
    const previews: Record<string, string> = {};
    let pending = 0;
    for (const item of this.items()) {
      const id = item.profileImageId?.trim();
      if (!id) continue;
      pending += 1;
      this.media.getById(id).subscribe({
        next: (asset) => {
          const url = this.mediaAssetPreviewUrl(asset);
          if (url) previews[item._id] = url;
          pending -= 1;
          if (pending === 0) this.profilePreviewUrls.set({ ...previews });
        },
        error: () => {
          pending -= 1;
          if (pending === 0) this.profilePreviewUrls.set({ ...previews });
        },
      });
    }
    if (pending === 0) this.profilePreviewUrls.set(previews);
  }

  private resetProfileUpload(): void {
    this.profileFile = null;
    this.revokeLocalPreview();
    this.profilePreviewUrl.set(null);
    this.profilePhotoStatus.set('none');
    this.profileUploading.set(false);
    this.profileUploadMessage.set(null);
  }

  private loadProfilePreview(assetId?: string): void {
    const id = assetId?.trim();
    if (!id) {
      this.profilePreviewUrl.set(null);
      this.profilePhotoStatus.set('none');
      return;
    }
    this.media.getById(id).subscribe({
      next: (asset) => {
        const previewUrl = this.mediaAssetPreviewUrl(asset);
        this.profilePreviewUrl.set(previewUrl || null);
        this.profilePhotoStatus.set(previewUrl ? 'saved' : 'none');
      },
      error: () => {
        this.profilePreviewUrl.set(null);
        this.profilePhotoStatus.set('none');
      },
    });
  }

  private revokeLocalPreview(): void {
    if (this.localPreviewObjectUrl) {
      URL.revokeObjectURL(this.localPreviewObjectUrl);
      this.localPreviewObjectUrl = null;
    }
  }

  private mediaAssetId(asset: MediaAssetDto): string {
    const raw = asset._id ?? (asset as MediaAssetDto & { id?: string }).id;
    return raw != null ? String(raw) : '';
  }

  private mediaAssetPreviewUrl(asset: MediaAssetDto): string {
    const cdnUrl = (asset as MediaAssetDto & { cdnUrl?: string }).cdnUrl?.trim();
    const url = asset.url?.trim();
    return cdnUrl || url || '';
  }
}
